import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@mui/material';
import { CameraDevice, Html5Qrcode } from 'html5-qrcode';
import React, { useEffect, useRef, useState } from 'react';
import { Timestamp, addDoc, collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';

import { Voter } from '../../types';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

type ScanPurpose = 'Stub Release' | 'Final Release' | 'Exit Poll';

const ScanVoterQR: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  // Get the purpose from route state, redirect if not provided
  const scanPurpose = location.state?.purpose as ScanPurpose | undefined;

  const [voter, setVoter] = useState<Voter | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraId, setCameraId] = useState<string>('');
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  
  const [releaseType, setReleaseType] = useState('direct');
  const [voterPreference, setVoterPreference] = useState('straight');
  const [hasExistingEntry, setHasExistingEntry] = useState(false);

  const qrCodeScannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement | null>(null);
  const isScanningRef = useRef(false);
  const scanRestartTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Redirect if no purpose was selected
  useEffect(() => {
    if (!scanPurpose) {
      navigate('/scan-purpose');
    }
  }, [scanPurpose, navigate]);

  useEffect(() => {
    // List available cameras
    Html5Qrcode.getCameras()
      .then(devices => {
        setCameras(devices);
        if (devices.length > 0) {
          setCameraId(devices[0].id);
        }
      })
      .catch(err => {
        setError(`Error getting cameras: ${err.message}`);
      });

    // Add visibility change event listener
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopScanner();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup scanner on component unmount
      stopScanner();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Clear any pending restart timeout
      if (scanRestartTimeoutRef.current) {
        clearTimeout(scanRestartTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Handle camera switching
    const switchCamera = async () => {
      try {
        // Stop any existing scanner
        await stopScanner();

        // Start new scanner with selected camera
        if (cameraId && scannerContainerRef.current) {
          startScanner();
        }
      } catch (err) {
        setError(`Error switching camera: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };

    switchCamera();
  }, [cameraId]);

  const checkExistingEntry = async (voterId: string) => {
    if (!scanPurpose) return false;

    try {
      let collectionName: string;
      switch (scanPurpose) {
        case 'Stub Release':
          collectionName = 'stubReleases';
          break;
        case 'Final Release':
          collectionName = 'finalReleases';
          break;
        case 'Exit Poll':
          collectionName = 'exitPolls';
          break;
        default:
          return false;
      }

      const q = query(
        collection(db, collectionName), 
        where('voterId', '==', voterId)
      );

      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (err) {
      console.error('Error checking existing entry:', err);
      return false;
    }
  };

  const addVoterEntry = async (voter: Voter) => {
    if (!user) {
      console.error('User not authenticated');
      setError('User not authenticated');
      return;
    }

    try {
      let collectionName: string;
      const baseEntry = {
        voterId: voter.id,
        voterName: voter.name,
        precinct: voter.precinct,
        barangay: voter.barangay,
        city_municipality: voter.city_municipality,
        province: voter.province,
        district: voter.district,
        addedBy: user.display_name ?? user.email ?? user.uid,
        addedAt: Timestamp.now()
      };

      switch (scanPurpose) {
        case 'Stub Release':
          collectionName = 'stubReleases';
          await addDoc(collection(db, collectionName), {
            ...baseEntry,
            releaseType,
            voterPreference
          });
          console.log(`Stub release added for ${voter.name}`);
          break;
        case 'Final Release':
          collectionName = 'finalReleases';
          await addDoc(collection(db, collectionName), baseEntry);
          console.log(`Final release added for ${voter.name}`);
          break;
        case 'Exit Poll':
          collectionName = 'exitPolls';
          await addDoc(collection(db, collectionName), baseEntry);
          console.log(`Exit poll added for ${voter.name}`);
          break;
        default:
          throw new Error('Invalid scan purpose');
      }
    } catch (error) {
      console.error(`Error adding ${scanPurpose}:`, error);
      setError(`Failed to add ${scanPurpose} entry`);
    }
  };

  const fetchVoter = async (voterId: string) => {
    try {
      const voterDocRef = doc(db, 'voters', voterId);
      const voterDoc = await getDoc(voterDocRef);

      if (voterDoc.exists()) {
        const voterData = voterDoc.data() as Voter;
        const existingEntry = await checkExistingEntry(voterId);
        
        const voterWithId = {...voterData, id: voterId};

        // If no existing entry, automatically add it
        if (!existingEntry) {
          await addVoterEntry(voterWithId);
        }

        setVoter(voterWithId);
        setHasExistingEntry(existingEntry);
      } else {
        setError('Voter not found in database');
      }
    } catch (err) {
      setError('Error fetching voter information');
      console.error(err);
    }
  };

  const startScanner = () => {
    // Prevent multiple scanner starts using ref
    if (isScanningRef.current || !cameraId || !scannerContainerRef.current) return;

    const html5QrCode = new Html5Qrcode(scannerContainerRef.current.id);
    qrCodeScannerRef.current = html5QrCode;

    html5QrCode.start(
      cameraId, 
      {
        fps: 10,
        qrbox: 250
      },
      onScanSuccess,
      onScanError
    ).then(() => {
      isScanningRef.current = true;
      setError(null);
    }).catch(err => {
      setError(`Error starting scanner: ${err.message}`);
      isScanningRef.current = false;
      qrCodeScannerRef.current = null;
    });
  };

  const stopScanner = async () => {
    if (qrCodeScannerRef.current) {
      try {
        await qrCodeScannerRef.current.stop();
        qrCodeScannerRef.current.clear();
        qrCodeScannerRef.current = null;
        isScanningRef.current = false;
      } catch (err) {
        console.error('Exception stopping scanner:', err);
        qrCodeScannerRef.current = null;
        isScanningRef.current = false;
      }
    }
  };

  const onScanSuccess = async (decodedText: string) => {
    try {
      stopScanner();
      await fetchVoter(decodedText);
      
      // a little delay before restarting scanner
      scanRestartTimeoutRef.current = setTimeout(() => {
        startScanner();
      }, 800);
    } catch (err) {
      setError('Invalid QR code format');
    }
  };

  const onScanError = (errorMessage: string) => {
    console.debug(`Scan error: ${errorMessage}`);
  };

  const handleClose = () => {
    setVoter(null);
    setError(null);
    setHasExistingEntry(false);
    startScanner(); // Restart scanner after closing dialog
  };

  // If no purpose selected, don't render anything
  if (!scanPurpose) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        my: 4, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2 
      }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Scan Voter QR - {scanPurpose}
        </Typography>
        
        {scanPurpose === 'Stub Release' && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Release Type</InputLabel>
                  <Select
                    value={releaseType}
                    label="Release Type"
                    onChange={(e) => setReleaseType(e.target.value)}
                  >
                    <MenuItem value="direct">Direct Release</MenuItem>
                    <MenuItem value="leader">Leader Released</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Voter Preference</InputLabel>
                  <Select
                    value={voterPreference}
                    label="Voter Preference"
                    onChange={(e) => setVoterPreference(e.target.value)}
                  >
                    <MenuItem value="straight">Straight Voting</MenuItem>
                    <MenuItem value="organization">Exclusive to an Organization</MenuItem>
                    <MenuItem value="position">Exclusive to a Specific Position</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {cameras.length > 0 && (
          <FormControl fullWidth>
            <InputLabel>Select Camera</InputLabel>
            <Select
              value={cameraId}
              label="Select Camera"
              onChange={(e) => setCameraId(e.target.value)}
            >
              {cameras.map((camera, index) => (
                <MenuItem key={camera.id} value={camera.id}>
                  {camera.label || `Camera ${index + 1}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box 
          ref={scannerContainerRef} 
          id="scanner-container" 
          sx={{ 
            width: '100%', 
            height: '400px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            border: '1px solid grey',
            overflow: 'hidden'
          }}
        />

        {error && (
          <Alert 
            severity="error" 
            onClose={handleClose}
            sx={{ mt: 2 }}
          >
            {error}
          </Alert>
        )}

        {voter && (
          <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
            <Typography variant="h6">Voter Details</Typography>
            <Typography>Name: {voter.name}</Typography>
            <Typography>Precinct: {voter.precinct}</Typography>
            <Typography>Barangay: {voter.barangay}</Typography>
            <Typography>Municipality: {voter.city_municipality}</Typography>
            
            {hasExistingEntry && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This voter has an existing entry for this scan purpose.
              </Alert>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default ScanVoterQR;
